'use client';

import React, { useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

const features = [
    'Free forever for solo devs',
    'No credit card required',
    'CLI + VS Code extension',
    'Works with any git repo',
];

export default function CTASection() {
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && sectionRef.current) {
                        sectionRef.current.style.opacity = '1';
                        sectionRef.current.style.transform = 'translateY(0)';
                    }
                });
            },
            { threshold: 0.2 }
        );
        if (sectionRef?.current) observer?.observe(sectionRef?.current);
        return () => observer?.disconnect();
    }, []);

    return (
        <section id="cta" className="py-24 px-6 relative overflow-hidden">
            {/* Background blobs */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] blob-green opacity-20 pointer-events-none" />
            <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] blob-cyan opacity-15 pointer-events-none" />

            <div className="max-w-7xl mx-auto relative z-10">
                <div
                    ref={sectionRef}
                    className="glass-panel rounded-3xl border border-border p-10 md:p-16 relative overflow-hidden transition-all duration-700"
                    style={{ opacity: 1, transform: 'translateY(0)' }}
                >
                    {/* Inner glow */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none rounded-3xl" />

                    <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
                        {/* Left */}
                        <div>
                            <p className="section-label mb-4">Get Started Today</p>
                            <h2 className="text-section-heading font-extrabold text-foreground mb-5">
                                Stop reverting files.{' '}
                                <span className="text-gradient-primary italic font-light">
                                    Start restoring code.
                                </span>
                            </h2>
                            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                                Join 12+ developers who&apos;ve stopped losing work to file-level reverts.
                                Install in 30 seconds, start tracking in under a minute.
                            </p>

                            {/* Feature list */}
                            <ul className="space-y-3 mb-8">
                                {features?.map((f, i) => (
                                    <li key={i} className="flex items-center gap-3">
                                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                            <Icon name="CheckIcon" size={12} className="text-primary" />
                                        </div>
                                        <span className="text-sm text-muted-foreground">{f}</span>
                                    </li>
                                ))}
                            </ul>

                            {/* CTAs */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <a href="https://marketplace.visualstudio.com/items?itemName=CodigoFoundry.capstate-latest&ssr=false#review-details" target="_blank" className="btn-primary">
                                    Get Started Free
                                    <Icon name="ArrowRightIcon" size={16} />
                                </a>
                                <a href="https://github.com/Codigo-Ng/Cap-state-ext/blob/main/README.md" target='_blank' className="btn-secondary">
                                    <Icon name="BookOpenIcon" size={16} className="text-accent" />
                                    Read the Docs
                                </a>
                            </div>
                        </div>

                        {/* Right: Terminal install card */}
                        <div className="space-y-4">
                            {/* Install command */}
                            <div className="bg-card rounded-2xl border border-border p-5">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500/60" />
                                        <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                                        <div className="w-3 h-3 rounded-full bg-green-500/60" />
                                    </div>
                                    <span className="font-mono-code text-xs text-muted-foreground">terminal</span>
                                </div>
                                <div className="code-block space-y-2">
                                    <p>
                                        <span className="text-muted-foreground">$ </span>
                                        <span className="text-foreground/80">npm install -g capstate</span>
                                    </p>
                                    <p>
                                        <span className="text-muted-foreground">$ </span>
                                        <span className="text-foreground/80">capstate init</span>
                                    </p>
                                    <p className="text-primary">✓ CapState initialized in ./src</p>
                                    <p className="text-muted-foreground">
                                        Tracking 0 blocks. Run{' '}
                                        <span className="text-accent">capstate add</span> to start.
                                    </p>
                                    <p>
                                        <span className="text-accent">$ </span>
                                        <span className="text-foreground/80">capstate add calculatePrice</span>
                                    </p>
                                    <p className="text-primary">
                                        ✓ Now tracking calculatePrice
                                        <span className="animate-cursor-blink ml-1">▌</span>
                                    </p>
                                </div>
                            </div>

                            {/* Quick stats */}
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { val: '30s', label: 'to install' },
                                    { val: '1 cmd', label: 'to restore' },
                                    { val: '0 deps', label: 'required' },
                                ]?.map((item, i) => (
                                    <div key={i} className="bg-card rounded-xl border border-border p-3 text-center">
                                        <p className="text-lg font-bold text-gradient-primary">{item?.val}</p>
                                        <p className="font-mono-code text-xs text-muted-foreground">{item?.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}